const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
const DOCX_EXTENSIONS = [".docx"];
const XLSX_EXTENSIONS = [".xls", ".xlsx"];
const CSV_EXTENSIONS = [".csv"];

export function getTokenFromCookie() {
  const cookies = document.cookie.split(";");
  const tokenCookie = cookies.find((cookie) => cookie.trim().startsWith("token="));
  return tokenCookie ? tokenCookie.split("=")[1] : null;
}

export function getFilePath(file) {
  if (!file) return "";
  if (typeof file === "string") return file;
  if (file.path) return file.path;
  if (typeof file.content === "string" && file.content.startsWith("s3://")) {
    return file.content;
  }
  return "";
}

export function getFileName(file) {
  if (!file) return "Uploaded file";
  if (typeof file === "string") {
    return file.split("/").pop() || "Uploaded file";
  }
  if (file.name) return file.name;
  return getFilePath(file).split("/").pop() || "Uploaded file";
}

export function getFileType(file) {
  if (!file || typeof file === "string") return "";
  return file.type || "";
}

export function isStoredFileReference(file) {
  const filePath = getFilePath(file);
  return filePath.startsWith("s3://");
}

function hasExtension(name, extensions) {
  const lowerName = String(name || "").toLowerCase();
  return extensions.some((extension) => lowerName.endsWith(extension));
}

export function getFileCategory(file) {
  const name = getFileName(file).toLowerCase();
  const type = getFileType(file).toLowerCase();

  if (type.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (type.startsWith("image/") || hasExtension(name, IMAGE_EXTENSIONS)) return "image";
  if (type.includes("officedocument.wordprocessingml.document") || hasExtension(name, DOCX_EXTENSIONS)) return "docx";
  if (
    type.includes("officedocument.spreadsheetml.sheet") ||
    type.includes("ms-excel") ||
    hasExtension(name, XLSX_EXTENSIONS)
  ) {
    return "xlsx";
  }
  if (type.includes("text/csv") || hasExtension(name, CSV_EXTENSIONS)) return "csv";
  if (type.startsWith("audio/")) return "audio";
  if (type.startsWith("video/")) return "video";
  return "other";
}

export function sanitizeStoredFile(file) {
  if (!file || typeof file !== "object") {
    return file;
  }

  const filePath = file.path || 
    (typeof file.content === "string" && file.content.startsWith("s3://") ? file.content : null);

  if (!filePath) return null;

  return {
    name: file.name || file.file?.name || getFileName(file),
    type: file.type || file.file?.type || "",
    size: Number(file.size || 0),
    path: filePath,
  };
}

export function sanitizeFileAnswer(answer) {
  if (Array.isArray(answer)) {
    return answer.map(sanitizeStoredFile).filter(Boolean);
  }

  if (answer && typeof answer === "object") {
    return sanitizeStoredFile(answer);
  }

  return answer;
}

async function fetchJson(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "File request failed");
  }
  return response.json();
}

export async function uploadFileToStorage({
  file,
  formId,
  encryptedFormId,
  questionName,
  token,
}) {
  const payload = {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    fileSize: file.size || 0,
    questionName,
  };

  if (formId) {
    payload.formId = formId;
  }

  if (encryptedFormId) {
    payload.encryptedFormId = encryptedFormId;
  }

  const uploadTarget = await fetchJson(`${API_BASE_URL}/files/presign-upload`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  let uploadResponse;

  // Preferred: presigned POST (request URL is clean; auth stays in form fields).
  if (uploadTarget.upload?.method === "POST" && uploadTarget.upload?.url && uploadTarget.upload?.fields) {
    const formData = new FormData();
    Object.entries(uploadTarget.upload.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    uploadResponse = await fetch(uploadTarget.upload.url, {
      method: "POST",
      body: formData,
    });
  } else {
    // Fallback: presigned PUT
    uploadResponse = await fetch(uploadTarget.uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });
  }

  if (!uploadResponse.ok) {
    throw new Error("Failed to upload file to storage");
  }

  return uploadTarget.file;
}

export async function fetchSignedFileUrl({
  file,
  shareToken,
  token,
  download = false,
}) {
  const filePath = getFilePath(file);
  if (!filePath) {
    throw new Error("Missing file path");
  }

  // Default: stream via backend so the browser never sees the S3 signed URL.
  // Set `REACT_APP_DIRECT_S3_SIGNED_URLS=true` only if you explicitly want the signed URL in the browser.
  const directSignedUrls = String(process.env.REACT_APP_DIRECT_S3_SIGNED_URLS || "").toLowerCase() === "true";

  if (!directSignedUrls) {
    const streamUrl = new URL(`${API_BASE_URL}/files/stream`);
    streamUrl.searchParams.set("path", filePath);
    if (download) streamUrl.searchParams.set("download", "1");
    if (shareToken) streamUrl.searchParams.set("shareToken", shareToken);
    return streamUrl.toString();
  }

  const result = await fetchJson(`${API_BASE_URL}/files/access-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      path: filePath,
      fileName: getFileName(file),
      shareToken,
      download,
    }),
  });

  return result.url;
}

export async function fetchFileArrayBuffer({ file, shareToken, token }) {
  const signedUrl = await fetchSignedFileUrl({ file, shareToken, token });
  const isBackendStream = signedUrl.startsWith(String(API_BASE_URL || ""));
  const response = await fetch(signedUrl, {
    headers: isBackendStream && token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch file");
  }
  return response.arrayBuffer();
}

export async function fetchFileText({ file, shareToken, token }) {
  const signedUrl = await fetchSignedFileUrl({ file, shareToken, token });
  const isBackendStream = signedUrl.startsWith(String(API_BASE_URL || ""));
  const response = await fetch(signedUrl, {
    headers: isBackendStream && token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch file");
  }
  return response.text();
}

export async function deleteUploadedFiles({
  files,
  formId,
  encryptedFormId,
  token,
}) {
  const payload = {
    files: (Array.isArray(files) ? files : []).map(sanitizeStoredFile),
  };

  if (formId) {
    payload.formId = formId;
  }

  if (encryptedFormId) {
    payload.encryptedFormId = encryptedFormId;
  }

  await fetchJson(`${API_BASE_URL}/files/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
}
